import type { SailPlotExtensions } from "@heiselanalytics/sailplot";
import { HeiselAnalyticsPageFooter } from "../components/HeiselAnalyticsPageFooter";
import { heiselLegalOperator, type LegalOperator } from "../config/legal";
import { platformVersionLabel } from "../config/version";
import { ChangelogPage } from "../pages/ChangelogPage";
import { InstructionsPage } from "../pages/InstructionsPage";
import { createImprintPage } from "../pages/ImprintPage";
import { MakeYourOwnPage } from "../pages/MakeYourOwnPage";
import { ManageSubscriptionPage } from "../pages/ManageSubscriptionPage";
import { OpenSourcePage } from "../pages/OpenSourcePage";
import { createPrivacyPage } from "../pages/PrivacyPage";
import { createRefundPolicyPage } from "../pages/RefundPolicyPage";
import { createTermsPage } from "../pages/TermsPage";
import { createContractActionPage } from "../pages/ContractActionPage";

interface PlatformPageOptions {
  customerLegalLinks?: {
    imprint?: string;
    privacy?: string;
  };
  hostingProvider?: "cloudflare-pages" | "github-pages";
  imprint?: boolean;
  instructions?: boolean;
  makeYourOwn?: boolean;
  openSource?: boolean;
  privacy?: boolean;
  sailPlotLegalBaseUrl?: string;
  operator?: LegalOperator;
  paddlePayments?: boolean;
  hostedForCustomer?: boolean;
  improvementRequests?: boolean;
}

export function createPlatformPageExtensions({
  customerLegalLinks,
  hostingProvider = "github-pages",
  imprint = true,
  instructions = false,
  makeYourOwn = false,
  openSource = false,
  privacy = true,
  sailPlotLegalBaseUrl,
  operator = heiselLegalOperator,
  paddlePayments = false,
  hostedForCustomer = false,
  improvementRequests = false,
}: PlatformPageOptions = {}): SailPlotExtensions {
  const sailPlotPrivacyUrl = sailPlotLegalBaseUrl
    ? new URL("privacy", sailPlotLegalBaseUrl).toString()
    : undefined;
  const sailPlotImprintUrl = sailPlotLegalBaseUrl
    ? new URL("imprint", sailPlotLegalBaseUrl).toString()
    : undefined;
  const imprintPage = createImprintPage({
    customerImprintUrl: customerLegalLinks?.imprint,
    hostedForCustomer,
    operator,
  });
  const privacyPage = createPrivacyPage({
    customerPrivacyUrl: customerLegalLinks?.privacy,
    hostedForCustomer,
    hostingProvider,
    operator,
    paddlePayments,
    improvementRequests,
  });
  const termsPage = createTermsPage({ operator });
  const refundPolicyPage = createRefundPolicyPage({ operator });
  const cancellationPage = createContractActionPage("cancel");
  const withdrawalPage = createContractActionPage("withdraw");

  return {
    pageFooter: HeiselAnalyticsPageFooter,
    routes: [
      ...(makeYourOwn
        ? [
            {
              path: "/make-your-own",
              title: "Make your own SailPlot",
              component: MakeYourOwnPage,
            },
          ]
        : []),
      ...(instructions
        ? [
            {
              path: "/instructions",
              title: "Instructions",
              component: InstructionsPage,
            },
          ]
        : []),
      {
        path: "/changelog",
        title: "Changelog",
        component: ChangelogPage,
      },
      ...(openSource
        ? [
            {
              path: "/open-source",
              title: "Open Source",
              component: OpenSourcePage,
            },
          ]
        : []),
      ...(paddlePayments
        ? [
            {
              path: "/manage-subscription",
              title: "Manage subscription",
              component: ManageSubscriptionPage,
            },
            {
              path: "/cancel",
              title: "Cancel contract",
              component: cancellationPage,
            },
            {
              path: "/withdraw",
              title: "Withdraw from contract",
              component: withdrawalPage,
            },
            {
              path: "/terms",
              title: "Terms of service",
              component: termsPage,
            },
            {
              path: "/refund-policy",
              title: "Refund policy",
              component: refundPolicyPage,
            },
          ]
        : []),
      ...(privacy
        ? [{ path: "/privacy", title: "Privacy", component: privacyPage }]
        : []),
      ...(imprint
        ? [{ path: "/imprint", title: "Legal Notice", component: imprintPage }]
        : []),
    ],
    navigationItems: [
      ...(makeYourOwn
        ? [
            {
              id: "make-your-own",
              label: "Make Your Own SailPlot",
              path: "/make-your-own",
            },
          ]
        : []),
      ...(paddlePayments
        ? [
            {
              id: "manage-subscription",
              label: "Subscription & Invoices",
              path: "/manage-subscription",
            },
          ]
        : []),
      ...(instructions
        ? [
            {
              id: "instructions",
              label: "Instructions",
              path: "/instructions",
            },
          ]
        : []),
      ...(openSource
        ? [
            {
              id: "open-source",
              label: "Open Source",
              path: "/open-source",
              separatorBefore: true,
              muted: true,
            },
          ]
        : []),
      ...(hostedForCustomer && customerLegalLinks?.privacy
        ? [
            {
              id: "customer-privacy",
              label: "Operator Privacy",
              href: customerLegalLinks.privacy,
              external: true,
              muted: true,
            },
          ]
        : []),
      ...(hostedForCustomer && customerLegalLinks?.imprint
        ? [
            {
              id: "customer-imprint",
              label: "Operator Legal Notice",
              href: customerLegalLinks.imprint,
              external: true,
              muted: true,
            },
          ]
        : []),
      ...(paddlePayments
        ? [
            {
              id: "terms",
              label: "Terms of Service",
              path: "/terms",
              muted: true,
            },
            {
              id: "refund-policy",
              label: "Refund Policy",
              path: "/refund-policy",
              muted: true,
            },
          ]
        : []),
      ...(hostedForCustomer && sailPlotPrivacyUrl
        ? [
            {
              id: "privacy",
              label: "SailPlot Privacy",
              href: sailPlotPrivacyUrl,
              external: true,
              muted: true,
            },
          ]
        : privacy
          ? [
              {
                id: "privacy",
                label: "Privacy",
                path: "/privacy",
                muted: true,
              },
            ]
          : []),
      ...(hostedForCustomer && sailPlotImprintUrl
        ? [
            {
              id: "imprint",
              label: "SailPlot Legal Notice",
              href: sailPlotImprintUrl,
              external: true,
              muted: true,
            },
          ]
        : imprint
          ? [
              {
                id: "imprint",
                label: "Legal Notice",
                path: "/imprint",
                muted: true,
              },
            ]
          : []),
      ...(paddlePayments
        ? [
            {
              id: "cancel-contract",
              label: "Cancel Contract",
              path: "/cancel",
              muted: true,
            },
            {
              id: "withdraw-contract",
              label: "Withdraw from Contract",
              path: "/withdraw",
              muted: true,
            },
          ]
        : []),
      {
        id: "version",
        label: platformVersionLabel,
        path: "/changelog",
        separatorBefore: true,
        muted: true,
        version: true,
      },
    ],
  };
}

export const platformPageExtensions = createPlatformPageExtensions();
